import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  message,
  Popconfirm,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  CameraOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SendOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { quotationApi } from '@/services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const QuotationList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // 篩選條件
  const [filters, setFilters] = useState({
    search: '',
    status: null,
    dateRange: null
  });

  useEffect(() => {
    loadQuotations();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadQuotations = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        status: filters.status || undefined,
        startDate: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: filters.dateRange?.[1]?.format('YYYY-MM-DD')
      };

      const response = await quotationApi.list(params);

      if (response.data.success) {
        setQuotations(response.data.data);
        setPagination({
          ...pagination,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      message.error('載入報價單失敗');
    } finally {
      setLoading(false);
    }
  };

  // 狀態標籤
  const getStatusTag = (status) => {
    const statusConfig = {
      draft: { color: 'default', text: '草稿' },
      sent: { color: 'blue', text: '已送出' },
      viewed: { color: 'cyan', text: '已查看' },
      accepted: { color: 'green', text: '已接受' },
      rejected: { color: 'red', text: '已拒絕' },
      expired: { color: 'orange', text: '已過期' },
      converted: { color: 'success', text: '已轉單' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 刪除報價單
  const handleDelete = async (id) => {
    try {
      const response = await quotationApi.delete(id);
      if (response.data.success) {
        message.success('刪除成功');
        loadQuotations();
      }
    } catch (error) {
      message.error(error.response?.data?.error || '刪除失敗');
    }
  };

  // 發送報價單
  const handleSend = async (id) => {
    try {
      const response = await quotationApi.send(id, 'email');
      if (response.data.success) {
        message.success('報價單已發送');
        loadQuotations();
      }
    } catch (error) {
      message.error('發送失敗');
    }
  };

  // 轉訂單
  const handleConvertToOrder = async (id) => {
    try {
      const response = await quotationApi.convertToOrder(id);
      if (response.data.success) {
        message.success('已轉為訂單');
        navigate(`/orders/${response.data.data.id}`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || '轉單失敗');
    }
  };

  // 表格欄位
  const columns = [
    {
      title: '報價單號',
      dataIndex: 'quotation_no',
      key: 'quotation_no',
      width: 150,
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/quotations/${record.id}`)}
        >
          {text}
        </Button>
      )
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      render: (version) => version > 1 ? <Tag>V{version}</Tag> : null
    },
    {
      title: '客戶',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 150,
      render: (text, record) => (
        <div>
          <div>{text}</div>
          {record.customer?.company_name && (
            <div style={{ fontSize: 12, color: '#999' }}>
              {record.customer.company_name}
            </div>
          )}
        </div>
      )
    },
    {
      title: '金額（未稅）',
      dataIndex: 'subtotal_excl_tax',
      key: 'subtotal_excl_tax',
      width: 130,
      align: 'right',
      render: (amount) => `NT$ ${parseFloat(amount).toLocaleString()}`
    },
    {
      title: '總金額',
      dataIndex: 'grand_total',
      key: 'grand_total',
      width: 130,
      align: 'right',
      render: (amount) => (
        <strong>NT$ {parseFloat(amount).toLocaleString()}</strong>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '有效期限',
      dataIndex: 'valid_until',
      key: 'valid_until',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: '業務',
      dataIndex: ['salesRep', 'full_name'],
      key: 'salesRep',
      width: 100
    },
    {
      title: '建立時間',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/quotations/${record.id}`)}
            />
          </Tooltip>
          
          {record.status === 'draft' && (
            <>
              <Tooltip title="編輯">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/quotations/${record.id}/edit`)}
                />
              </Tooltip>
              <Tooltip title="發送">
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  onClick={() => handleSend(record.id)}
                />
              </Tooltip>
            </>
          )}
          
          {record.status === 'accepted' && (
            <Tooltip title="轉訂單">
              <Button
                type="text"
                icon={<SwapOutlined />}
                style={{ color: '#52c41a' }}
                onClick={() => handleConvertToOrder(record.id)}
              />
            </Tooltip>
          )}
          
          <Tooltip title="下載 PDF">
            <Button
              type="text"
              icon={<FilePdfOutlined />}
              onClick={() => window.open(`/api/quotations/${record.id}/pdf`)}
            />
          </Tooltip>
          
          {record.status === 'draft' && (
            <Popconfirm
              title="確定要刪除此報價單嗎？"
              onConfirm={() => handleDelete(record.id)}
              okText="確定"
              cancelText="取消"
            >
              <Tooltip title="刪除">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card>
        {/* 篩選工具列 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="搜尋報價單號、客戶名稱"
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            onPressEnter={loadQuotations}
          />
          
          <Select
            placeholder="狀態篩選"
            style={{ width: 150 }}
            allowClear
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
          >
            <Select.Option value="draft">草稿</Select.Option>
            <Select.Option value="sent">已送出</Select.Option>
            <Select.Option value="viewed">已查看</Select.Option>
            <Select.Option value="accepted">已接受</Select.Option>
            <Select.Option value="rejected">已拒絕</Select.Option>
            <Select.Option value="expired">已過期</Select.Option>
            <Select.Option value="converted">已轉單</Select.Option>
          </Select>
          
          <RangePicker
            placeholder={['開始日期', '結束日期']}
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          
          <Button onClick={loadQuotations}>查詢</Button>
          
          <Button
            onClick={() => setFilters({ search: '', status: null, dateRange: null })}
          >
            重置
          </Button>
        </Space>

        {/* 動作按鈕 */}
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/quotations/create')}
          >
            新增報價單
          </Button>
          <Button
            icon={<CameraOutlined />}
            onClick={() => navigate('/quotations/ocr')}
          >
            OCR 快速建單
          </Button>
        </Space>

        {/* 報價單表格 */}
        <Table
          columns={columns}
          dataSource={quotations}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 筆`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
            }
          }}
          scroll={{ x: 1500 }}
        />
      </Card>
    </div>
  );
};

export default QuotationList;
